import { Checkbox } from "@mantine/core";
import { type ReactNode } from "react";
import { DateTimeDisp } from "src/components/shared/DateTime";
import { ICol } from "src/interfaces/Logbooks";
import { useSelector } from "src/redux/hooks";
import { Flex, StyledTd, StyledTr } from "src/styled";

import type { INMEARow } from "./NMEAAutomationModal.utils";
import type { INMEASection } from "./NMEAAutomationModal.utils";
import { useNMEAPreviewCheckboxNavigation } from "./useNMEAPreviewCheckboxNavigation";

export const isRowLockedFromUnselect = (
  lbentry: { NMEAData?: INMEASection[] | null } | null | undefined,
  section: INMEASection,
  row: INMEARow,
): boolean => {
  const savedSection = lbentry?.NMEAData?.find(
    s => s.originalSectionIndex === section.originalSectionIndex,
  );
  if (!savedSection?.rows?.length || !row.NMEAId) return false;

  const matchingSavedRow = savedSection.rows.find(
    savedRow => savedRow.NMEAId === row.NMEAId,
  );

  return Boolean(matchingSavedRow?.selected);
};

export const getColValueString = (col: ICol | string): string => {
  if (typeof col === "string" || !col) return "";
  let value = col?.value;
  if (typeof col?.value === "object" && col.value !== null) {
    if ("time" in col.value) {
      value = col.value.time;
    } else {
      value = col?.value?.value ?? col?.value ?? "";
    }
  }
  return String(value ?? "");
};

export const getComparableNMEACols = (nmeaRow: INMEARow): [string, ICol][] => {
  return Object.entries(nmeaRow.cols ?? {});
};

export const getSectionColumnKeys = (rows: INMEARow[]): [string, ICol][] => {
  if (!rows.length) return [];

  const rowWithMostCols = rows.reduce((best, row) =>
    Object.keys(row.cols ?? {}).length > Object.keys(best.cols ?? {}).length
      ? row
      : best,
  );

  return Object.entries(rowWithMostCols.cols ?? {});
};

export const isTimeColumn = (nmeaKey: string, col?: ICol) =>
  nmeaKey === "_ROW_DATE_TIME" ||
  nmeaKey === "ROW_TIME" ||
  col?.NMEA === "_ROW_DATE_TIME" ||
  col?.NMEA === "ROW_TIME";

export const renderBoldTimeValue = (content: ReactNode) => (
  <span style={{ fontWeight: 200 }}>{content}</span>
);

export const renderNMEATimeCell = (
  nmeaRow: INMEARow,
  nmeaCol?: ICol,
  isBridgeLog?: boolean,
) => {
  if (nmeaRow.dateTimeLT || nmeaRow.dateTimeUTC || nmeaRow.dateTime) {
    const lt =
      (nmeaRow.dateTimeLT?.includes("LT")
        ? nmeaRow.dateTimeLT.replace("LT", "")
        : nmeaRow.dateTimeLT) ?? nmeaRow.dateTime;
    return (
      <DateTimeDisp
        lt={lt}
        color="inherit"
        withSeconds={isBridgeLog}
        containerProps={{ style: { flexWrap: "nowrap" } }}
        textProps={{ fw: 200 }}
      />
    );
  }

  if (nmeaCol) {
    return renderBoldTimeValue(
      <>
        {getColValueString(nmeaCol)} {nmeaCol.unit === "24lock" && "LT"}
      </>,
    );
  }

  return "";
};

export const renderNMEACellValue = (
  nmeaRow: INMEARow,
  nmeaKey: string,
  headerCol: ICol,
  isBridgeLog?: boolean,
) => {
  const nmeaCol = nmeaRow.cols?.[nmeaKey];

  if (isTimeColumn(nmeaKey, nmeaCol ?? headerCol)) {
    return renderNMEATimeCell(nmeaRow, nmeaCol, isBridgeLog);
  }

  if (!nmeaCol) return "";

  return (
    <>
      {getColValueString(nmeaCol)} {nmeaCol.unit === "24lock" && "LT"}
    </>
  );
};

interface NMEAPreviewNMEARowProps {
  nmeaRow: INMEARow;
  selected: boolean;
  onToggle: (checked: boolean) => void;
  disabled?: boolean;
  columnKeys?: [string, ICol][];
  navigationId?: string;
}

export const NMEAPreviewNMEARow = ({
  nmeaRow,
  selected,
  onToggle,
  disabled,
  columnKeys,
  navigationId,
}: NMEAPreviewNMEARowProps) => {
  const { isBridgeLog } = useSelector(s => s.generatorSlice.lbentry);
  const { setCheckboxRef, onCheckboxKeyDown } =
    useNMEAPreviewCheckboxNavigation(navigationId);
  const colsToRender = columnKeys ?? getComparableNMEACols(nmeaRow);
  const nmeaId = nmeaRow.NMEAId;

  const handleRowClick = () => {
    if (disabled) return;
    onToggle(!selected);
  };

  return (
    <StyledTr
      hover={!disabled}
      pointer={!disabled}
      onClick={handleRowClick}
      key={`${nmeaId}-nmea`}>
      {colsToRender.map(([nmeaKey, headerCol]) => (
        <StyledTd
          key={`${nmeaId}-nmea-${nmeaKey}`}
          style={{
            whiteSpace: "nowrap",
            width: isTimeColumn(nmeaKey, headerCol) ? 1 : "auto",
          }}>
          {renderNMEACellValue(nmeaRow, nmeaKey, headerCol, isBridgeLog)}
        </StyledTd>
      ))}
      <StyledTd width="1px" onClick={e => e.stopPropagation()}>
        <Flex
          justify="center"
          align="center"
          h="100%"
          px={4}
          style={{ flexShrink: 1 }}>
          <Checkbox
            ref={navigationId ? setCheckboxRef : undefined}
            size="sm"
            checked={selected}
            disabled={disabled}
            onChange={event => onToggle(event.currentTarget.checked)}
            onKeyDown={navigationId ? onCheckboxKeyDown : undefined}
          />
        </Flex>
      </StyledTd>
    </StyledTr>
  );
};
